import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Icon } from '@evertrue/et-components';

/**
 * Dropdown select with a search/filter input.
 * Replaces native <select> for long lists of options (contact lists, saved searches).
 * Uses a portal to render the dropdown outside any overflow:hidden/auto containers
 * so it stays visible inside modals.
 *
 * @param {Object[]} options - Array of { label, value, count? }
 * @param {Object|null} value - Currently selected option object, or null
 * @param {Function} onChange - Called with the selected option object, or null to clear
 * @param {string} placeholder - Placeholder text when nothing is selected
 * @param {string} [className] - Additional class name for the wrapper
 * @param {boolean} [disabled] - Whether the select is disabled
 */
const SearchableSelect = ({ options = [], value, onChange, placeholder = 'Select...', className = '', disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = search ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase())) : options;

  const DROPDOWN_MAX_HEIGHT = 280;

  const computeDropdownStyle = () => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;

    return {
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      ...(openUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    };
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // Recompute position on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;
    const update = () => setDropdownStyle(computeDropdownStyle());
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      setDropdownStyle(computeDropdownStyle());
    }
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setSearch('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
    }
  };

  const dropdown = isOpen
    ? createPortal(
        <div className="searchable-select--dropdown" style={dropdownStyle} onMouseDown={(e) => e.stopPropagation()}>
          <div className="searchable-select--search-wrapper">
            <Icon icon="search" />
            <input
              ref={inputRef}
              type="text"
              className="searchable-select--search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search..."
            />
          </div>
          <div className="searchable-select--options">
            {filtered.length === 0 ? (
              <div className="searchable-select--empty">No results found</div>
            ) : (
              filtered.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`searchable-select--option ${value?.value === option.value ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  <span className="searchable-select--option-label">{option.label}</span>
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`searchable-select ${className} ${disabled ? 'is-disabled' : ''}`} ref={wrapperRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`searchable-select--trigger ${isOpen ? 'is-open' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={`searchable-select--trigger-text ${!value ? 'is-placeholder' : ''}`}>
          {value ? <>{value.label}</> : placeholder}
        </span>
        <span className="searchable-select--trigger-icons">
          {value && !disabled && (
            <button
              type="button"
              className="searchable-select--clear"
              onClick={handleClear}
              title="Clear selection"
              aria-label="Clear selection"
            >
              <Icon icon="close" />
            </button>
          )}
          <Icon icon={isOpen ? 'chevron-up' : 'chevron-down'} />
        </span>
      </button>
      {dropdown}
    </div>
  );
};

SearchableSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      count: PropTypes.number,
    })
  ),
  value: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    count: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

export default SearchableSelect;
